// Every expense string: the shared payment-state wording, the bearer question, and the
// overview screen that holds all of it.
//
// ITS OWN PART, MOVED OUT OF settlements.js. The two lived there because the gap screen
// reports what is still owed beside the balance, and while that was a few labels it cost
// nothing. The overview screen is its own area with its own vocabulary — two sections, an
// archive toggle, an attribution line per row — and settlements.js was 220 lines before
// any of it. Nothing about the keys changed: `he.expenses.*` and `he.officeExpenses.*`
// resolve exactly as before, which he.snapshot.test.js pins by value.
//
// Pure data — no imports, ever. errors.js reads he.leads.needContact at module scope,
// so a part reaching back for it would hand that read a half-built object. See he.js.
const expenses = {
  // Shared by both expense screens (a job's panel and the office list) and the modal
  // they both open, so the wording of the payment state is identical wherever it shows.
  expenses: {
    paymentState: 'סטטוס תשלום',
    paid: 'שולם',
    unpaid: 'טרם שולם',
    // ההוצאה ששולמה מהכיס של מי שאינו שותף (מיגרציה 061) — המזכירה שקנתה עוגות לפגישה.
    // מצב שלישי לצד "שולם" ו"טרם שולם", ולא וריאציה של אחד מהם: מבחינת העסק הכסף עדיין
    // לא יצא (אף שותף לא הוציא אותו), ולכן ההוצאה נספרת ברווח ואינה נכנסת לפער — אבל
    // מישהו כן חסר את הכסף, וזה מה שהשדה הזה מתעד.
    // ⚠ THE WORDING LEADS WITH THE PERSON, AND THAT IS THE WHOLE JOB OF THIS STRING.
    // It read "שולם מהכיס — להחזר", which describes a payment STATE, and the field it
    // sits in is labelled סטטוס תשלום — so somebody looking for "who paid" never opened
    // it. A partner went to the מי שילם dropdown to record a cost the secretary had paid,
    // found only the two owners there (correctly — that field is the money identity and
    // must never widen), and reported her as missing from the system. She was one option
    // away, behind a label written in the wrong frame.
    //
    // "שילם מישהו אחר" answers the question the reader is actually holding, and the
    // parenthesis keeps what the state MEANS without leading with it. The alternative
    // considered and dropped was a line of help text under the payer field: it explains
    // the maze instead of removing it, and it only works for somebody who reads it.
    fronted: 'שילם מישהו אחר (להחזר)',
    frontedBy: 'מי שילם מהכיס',
    frontedByLine: (name) => `שולם מהכיס של ${name} — להחזר`,
    // THE SAME COST AFTER THE REIMBURSEMENT, and it needs both names or it loses half of
    // what happened. Once a partner pays the fronter back the expense is marked paid with
    // that partner as payer, so the payer column would otherwise say only "רחל" — true,
    // and silent about the fact that somebody else went to the shop and was owed for it
    // until that day. The archive is where that is read, long after anyone remembers.
    reimbursedLine: (payer, fronter) => `${payer} — החזר ל${fronter}`,
    frontedRequired: 'יש לבחור מי שילם מהכיס.',
    // הכותרת של הסכום שמופיע לצד הפער במסך ההתחשבנות. במפורש לא חלק ממנו.
    owedToStaffTitle: 'חוב לעובדים על הוצאות מהכיס',
    owedToStaffRow: (name, count) => `${name} — ${count} הוצאות`,
    // Settling an expense from the row itself: one question, not the whole edit form.
    markPaid: 'סימון כשולם',
    markPaidTitle: 'סימון ההוצאה כשולמה',
    markPaidConfirm: 'סמן כשולם',
    whoPaid: 'מי שילם?',
    // One expense paid by both partners, each with their part (migration 069).
    payersBoth: 'שנינו',
    payerPart: (name) => `החלק של ${name}`,
    payerSplitMismatch: (total) => `החלקים צריכים להסתכם ב־${total}`,
    // Marking paid moves money between the partners, so the dialog still says so.
    markPaidHint: 'ההוצאה תיכנס להתחשבנות בין השותפים, לזכות מי ששילם.',
    // The breakdown under an expense total. Shown only when something is outstanding.
    ofWhichPaid: 'מזה שולם',
    ofWhichUnpaid: 'טרם שולם',
    // The other slice of that same total: the part the CLIENT bears, not yet charged on.
    // Says "לגבייה מהלקוח" and never "רווח" or "הכנסה" — it is money coming back, not
    // money earned, and nothing owes it until a charge is actually raised.
    ofWhichToRecover: 'מתוכן לגבייה מהלקוח',
    // The job's version, which knows both halves and names them.
    ofWhichSplit: 'מתוכן:',
    oursShort: 'נופלות עלינו',
    // THE NET LINE. Profit after only the costs we actually bear — the client-borne ones
    // come back, so they do not reduce it. Says "נטו" and never "רווח": the profit figure
    // beside it is the real one, and this is a reading of it, not a second version.
    netAfterOurs: 'נטו אחרי העלויות שנופלות עלינו:',
    netShort: 'נטו',
    // Why a billing job's expense list can sum higher than its expense tile: the costs we
    // absorb on it belong to the retainer's profit, not the job's. Said as the owner says
    // it — absorbed by us, and booked on the retainer — beside the pass-through tiles.
    onRetainerNote: 'עוד על חשבוננו, בריטיינר:',
    onRetainerShort: 'על חשבוננו',
    // The badge form. It says "עלויות לגבייה" in full rather than a bare "לגבייה"
    // because it sits beside a COST on the topics table and beside a PROFIT on the
    // retainers list, and next to a profit figure "לגבייה 1,000" would read as if part
    // of the profit were owed to us. Naming the costs makes it true in both places.
    toRecoverShort: 'עלויות לגבייה',
    // WHO BEARS THE COST (migration 054) — the second question a job expense answers,
    // and a different one from who PAID it. The payment state is about our money going
    // out; this is about where the cost finally lands. A cost can be paid and absorbed,
    // or unpaid and charged on.
    // Shown under an empty cost box, which blocks Save. The form has two money fields —
    // what it cost us and what we charge the client — and filling only the second used
    // to save a cost of zero in silence. A typed 0 is legitimate and says so.
    costRequired: 'יש להזין את העלות (0 אם לא עלה כסף).',
    borneBy: 'על מי נופלת העלות?',
    borneUs: 'עלינו',
    borneClient: 'על הלקוח',
    // A row recorded before the question was asked. Shown as its own answer rather than
    // folded into "נופלת עלינו", because we genuinely do not know which it was.
    borneUnstated: 'לא צוין',
    // Column header — short, in a table that is already expenses.
    borneByShort: 'מי משלם בסוף',
    chargeAmount: 'סכום לחיוב הלקוח',
    // A PRICE IS A CHARGE: saving it records the charge to collect in the same act.
    // Shown under the price the moment saving will raise the charge.
    chargeWillCreate: 'בשמירה יירשם חיוב לגבייה על הסכום הזה.',
    // The charge exists: the price and the bearer belong to the invoice now, and the
    // dialog shows that invoice and opens the money screen's own correct/cancel dialogs.
    chargeExisting: 'החיוב',
    chargeCorrect: 'תיקון החיוב',
    chargeCancel: 'ביטול החיוב',
    // Under the locked bearer: what has to happen first for it to change.
    bearerNeedsCancel: 'כדי לשנות על מי העלות, מבטלים קודם את החיוב.',
    bearerNeedsUnpay: 'נגבה כסף על החיוב. כדי לשנות על מי העלות, מבטלים קודם את התשלום במסך הכספים.',
    chargePreviewFailed: 'לא ניתן היה לבדוק אם השמירה תהפוך את העבודה לעבודה שמחייבת בעצמה. אם כן — השמירה תיכשל עד שיאושר המעבר.',
    // The two numbers side by side on an expense row: what we paid, what we charge.
    chargeShort: 'נבקש מהלקוח',
    // In the asked column: a client-borne cost with no price yet, so nothing is owed.
    chargeNotSet: 'עוד לא נקבע',
    // The charge's invoice was cancelled. Stated instead of its date and amount, because
    // a cancelled invoice is not money anybody owes and the money screen does not list it.
    chargeCancelled: 'החיוב בוטל',
    // Column header in the expense tables, beside "כמה זה עלה לנו". "ביקשנו", not "חויב":
    // the owner read "חויב" as "paid", when all it ever meant was that an invoice had been
    // recorded. Whether it has been PAID is the state badge in the bearer column.
    askedColumn: 'כמה ביקשנו מהלקוח',
  },
  officeExpenses: {
    title: 'הוצאות משרד',
    add: 'הוספת הוצאת משרד',
    empty: 'עדיין אין הוצאות משרד. לדוגמה: נסיעות, כוח אדם, רכישות קטנות.',
  },
  // THE ONE EXPENSE SCREEN. Office overhead and the costs on jobs are the same rows in
  // the same table (migration 026), so they are read in one place — the mirror of the
  // money screen, which answers who owes US. This one answers what WE still owe.
  expenseOverview: {
    title: 'הוצאות העסק',
    // The two sections. "משרד" keeps the old screen's word because it is the same thing
    // it always was; the other names what attaches a cost to work rather than to the
    // office, and says both kinds because a topic sits under a ריטיינר and a one-off does
    // not.
    officeSection: 'הוצאות משרד שוטפות',
    jobsSection: 'הוצאות על עבודות',
    // The toggle. Two buttons, the same pair the clients and retainers screens use, and
    // the words say which HALF is shown rather than what clicking does.
    showUnpaid: 'טרם שולמו',
    showPaid: 'ארכיון — ששולמו',
    // THE RED DOT'S LINE. Counted by the server over every unpaid row, so it says the
    // same thing in the archive as it does in the default view — an indicator that went
    // quiet when you opened the archive would go quiet precisely while the money is owed.
    stillOwed: (count, total) => `${count} הוצאות שטרם שולמו · ${total}`,
    nothingOwed: 'אין הוצאות פתוחות',
    // BENEATH THE HEADLINE, NEVER INSTEAD OF IT. The figure above is what we must pay
    // out — we owe the supplier the whole of it whoever ends up bearing the cost — and
    // these say how much of it comes back, so nobody reads an obligation as a spend.
    // The words are the ones the retainer and job tiles already use for the same set
    // (client-borne, no charge raised), so "לגבייה" cannot come to mean two things.
    ofWhichToRecoverLine: (amount) => `מתוכן לגבייה מהלקוח: ${amount}`,
    // The other half: client-borne and already invoiced. Not a job to do any more, but
    // still money coming back — so a reading of what the business actually spends has
    // to subtract it as well.
    alreadyChargedLine: (amount) => `כבר חויבו: ${amount}`,
    // Which engagement a cost sits under. The retainer when there is one, the client
    // otherwise, and the job itself always — a cost says where it came from without
    // anybody opening it.
    belongsTo: 'שייכת ל',
    vendor: 'למי שולם',
    vendorUnpaid: 'למי חייבים',
    emptyUnpaidOffice: 'אין הוצאות משרד שממתינות לתשלום.',
    emptyUnpaidJobs: 'אין הוצאות על עבודות שממתינות לתשלום.',
    emptyPaidOffice: 'אין הוצאות משרד ששולמו.',
    emptyPaidJobs: 'אין הוצאות על עבודות ששולמו.',
  },
};

export default expenses;
